import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import { PRICE_DIFFERENTIATION_MODE } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import type { ExtranetHotelExperience } from 'app/typings/core/extranet/hotel/experiences';
import type { ExtranetHotelPack } from 'app/typings/core/extranet/hotel/packs';

export type ExperiencePrices = {
  adultPrice: number;
  childPrice?: number;
  minPrice: number;
  maxPrice: number;
  isVariablePrice: boolean;
};
export const roundToOneDecimal = (value: number): number =>
  Math.round(value * 10) / 10;

export const floorToOneDecimal = (value: number): number =>
  Math.floor(value * 10) / 10;

export const discountToPrice = (
  originalPrice: number,
  discountPercentage: number
): number => roundToOneDecimal(originalPrice * (1 - discountPercentage / 100));

export const priceToDiscount = (
  originalPrice: number,
  discountedPrice: number
): number => {
  if (originalPrice <= 0) {
    return 0;
  }

  const percentage = (1 - discountedPrice / originalPrice) * 100;

  return floorToOneDecimal(Math.min(100, Math.max(0, percentage)));
};

const getMinMax = (values: (number | undefined)[]) => {
  const valid = values.filter(
    (value): value is number => value !== null && value !== undefined
  );

  if (!valid.length) {
    return undefined;
  }

  return { min: Math.min(...valid), max: Math.max(...valid) };
};

const toChildPrice = (price?: number): number | undefined =>
  price !== undefined && price > 0 ? price : undefined;

export const getExperiencePrices = (
  pack: ExtranetHotelPack,
  experience?: ExtranetHotelExperience
): ExperiencePrices | null => {
  const config = experience?.openingConfiguration?.priceConfiguration;

  if (!config) {
    return null;
  }

  const hasChildDiff =
    config.priceDifferentiationMode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD;

  const priceKeys = pack.isDaytime
    ? { adult: 'daytimePrice' as const, child: 'childDaytimePrice' as const }
    : { adult: 'price' as const, child: 'childPrice' as const };

  if (config.type === OPENING_CONFIGURATION_TYPE.CONSTANT) {
    const adultPrice = config.configuration[priceKeys.adult];

    if (adultPrice === null || adultPrice === undefined) {
      return null;
    }

    return {
      adultPrice,
      childPrice: hasChildDiff
        ? toChildPrice(config.configuration[priceKeys.child])
        : undefined,
      minPrice: adultPrice,
      maxPrice: adultPrice,
      isVariablePrice: false,
    };
  }

  const days = Object.values(config.days);
  const adultMinMax = getMinMax(days.map((day) => day[priceKeys.adult]));

  if (!adultMinMax) {
    return null;
  }

  const childPrice = hasChildDiff
    ? toChildPrice(getMinMax(days.map((day) => day[priceKeys.child]))?.min)
    : undefined;

  const allPaxPrices = [
    ...days.map((day) => day[priceKeys.adult]),
    ...(childPrice !== undefined
      ? days.map((day) => day[priceKeys.child])
      : []),
  ];
  const paxMinMax = getMinMax(allPaxPrices) ?? adultMinMax;

  return {
    adultPrice: adultMinMax.min,
    childPrice,
    minPrice: paxMinMax.min,
    maxPrice: paxMinMax.max,
    isVariablePrice: true,
  };
};
